import {assertAndContinue} from 'Asserts';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {resolveInputFileAndTransformsStatuses} from 'lab-automation/automation-files/getStatusForInputFileViewer';
import {FromServerRunOutputSamplesTable} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {FromServerResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {getStore} from 'redux-stores/Store';
import {assertNever} from 'util/Types';

import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import {
  AutomationFileStatusResponse,
  AutomationInputFileResponse,
  createAutomationInputFileBlob,
  fetchAutomationOutputFileAndStructuredTables,
  getAutomationFileStatus,
  getAutomationInputFileAndCopiedOutputFile,
  startProcessingAutomationOutputFile,
} from './AutomationFileDAO';

/**
 * Redux actions (and associated constants) for tracking automation file status.
 */

export enum AutomationFileStatusActionType {
  SET_AUTOMATION_FILE_STATUS = 'SET_AUTOMATION_FILE_STATUS',
}

interface AutomationFileStatusSet {
  type: AutomationFileStatusActionType.SET_AUTOMATION_FILE_STATUS;
  automationFileId: string;
  automationFileStatus: AutomationFileStatusGraphQL;
}

export function setAutomationFileStatus(
  automationFileId: string,
  automationFileStatus: AutomationFileStatusGraphQL
): void {
  getStore().dispatch({
    type: AutomationFileStatusActionType.SET_AUTOMATION_FILE_STATUS,
    automationFileId,
    automationFileStatus,
  });
}

export type AutomationFileStatusAction = AutomationFileStatusSet;

export const INITIAL_WAIT_TIME = 500; // exported for tests
const MAX_TOTAL_WAIT_TIME = 9 * 60 * 1000; // timeout of run_automation_output_processor and process_automation_output_file_task
const MAX_FAIL_RETRY_ATTEMPTS = 6;

function getStatusFromPollingResponse(response: AutomationFileStatusResponse): AutomationFileStatusGraphQL {
  // Output files stay in "RUNNING" status until processing is completed, Input files go to "SUCCEEDED" once
  // the initial file is generated, so we need to look at transform statuses to determine if generation is
  // actually done
  // eslint-disable-next-line benchlint/no-in-operator
  if ('automationOutputFileStatus' in response) {
    return response.automationOutputFileStatus;
  }
  // eslint-disable-next-line benchlint/no-in-operator
  if ('automationInputFileStatus' in response) {
    return resolveInputFileAndTransformsStatuses(
      response.automationInputFileStatus,
      response.automationTransformFileStatuses
    );
  }
  assertAndContinue(false, `Status response ${assertNever(response)} matches no automation file type`);
  return AutomationFileStatusGraphQL.pending;
}

function pollAutomationOutputFileStatus(
  automationOutputFileId: string,
  deferred: JQueryDeferred<unknown>,
  entryId?: string
): void {
  pollAutomationFileStatus(automationOutputFileId, deferred, () =>
    fetchAutomationOutputFileAndStructuredTables(automationOutputFileId, entryId)
  );
}

function pollAutomationInputFileStatus(
  automationInputFileId: string,
  deferred: JQueryDeferred<unknown>,
  copiedAutomationOutputFileId?: string
): void {
  pollAutomationFileStatus(automationInputFileId, deferred, () =>
    getAutomationInputFileAndCopiedOutputFile(automationInputFileId, copiedAutomationOutputFileId)
  );
}

function pollAutomationFileStatus<T>(
  automationFileId: string,
  deferred: JQueryDeferred<unknown>,
  finalResultLoader: () => Promise<T>,
  waitTime: number = INITIAL_WAIT_TIME,
  totalWaitTime: number = 0,
  failRetries: number = 0
): void {
  function retry(nextFailRetries: number): void {
    // Linearly increase waitTime
    const nextWaitTime = waitTime + 300;
    if (totalWaitTime > MAX_TOTAL_WAIT_TIME) {
      deferred.reject({userMessage: 'Your file is still processing; please check back in later.'});
      return;
    }

    setTimeout(() => {
      pollAutomationFileStatus(
        automationFileId,
        deferred,
        finalResultLoader,
        nextWaitTime,
        nextWaitTime + totalWaitTime,
        nextFailRetries
      );
    }, waitTime);
  }

  getAutomationFileStatus(automationFileId)
    .then((response) => {
      if (getStatusFromPollingResponse(response) === AutomationFileStatusGraphQL.running) {
        retry(failRetries);
      } else {
        finalResultLoader().then((data) => {
          deferred.resolve(data);
        });
      }
    })
    .catch((response) => {
      if (failRetries >= MAX_FAIL_RETRY_ATTEMPTS) {
        deferred.reject(response.error);
        return;
      }
      retry(failRetries + 1);
    });
}

export interface ProcessAutomationOutputFileResponse {
  automationOutputFile: AutomationOutputFileGraphQL;
  structuredTables: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>;
}

export function processAutomationOutputFile(
  automationOutputFileId: string,
  fileId?: string
): JQueryPromise<ProcessAutomationOutputFileResponse> {
  const result = startProcessingAutomationOutputFile(automationOutputFileId, fileId).then(() => {
    const deferred = $.Deferred<{
      automationOutputFile: AutomationOutputFileGraphQL;
      structuredTables: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>;
    }>();
    const promise = deferred.promise();
    pollAutomationOutputFileStatus(automationOutputFileId, deferred, fileId);
    return promise;
  });
  return result;
}

export function generateAutomationInputFile(
  assayRunId: string,
  configName: string,
  copyToOutputFile: boolean,
  username?: string | null
): Promise<AutomationInputFileResponse> {
  const result = createAutomationInputFileBlob(assayRunId, configName, copyToOutputFile, username).then(
    (initialResponse) => {
      if (initialResponse.errorMessage != null) {
        return initialResponse;
      }
      // If initial input file task does not error, poll until generation is done, including any transforms
      const deferred = $.Deferred<AutomationInputFileResponse>();
      const promise = deferred.promise();
      pollAutomationInputFileStatus(
        initialResponse.automationInputFile.id,
        deferred,
        copyToOutputFile ? initialResponse.automationOutputFile?.id : undefined
      );
      // Preserve instrumentQueryId from the initial backend response through the polling result
      return promise.then((finalResponse) => ({
        ...finalResponse,
        instrumentQueryId: initialResponse.instrumentQueryId,
      })) as JQueryPromise<AutomationInputFileResponse>;
    }
  );
  // With our current version of JQuery, resolving a JQueryPromise can return the specified type parameter or
  // `undefined`
  return result as Promise<AutomationInputFileResponse>;
}
